import { Injectable } from "@angular/core";
import { ComponentStore } from '@ngrx/component-store';import { tapResponse } from '@ngrx/operators';

import { Observable, switchMap } from "rxjs";
import { DatColumnMetadata, DatMapMetadataItem, DatMapMetadataModel, LexbeField } from '@models/resource/dat-map.resource';
import { DatMapSdk } from '@sdks/dat-map.sdk';
import { tap } from 'rxjs/operators';
import { WritableDraft, original, produce } from 'immer';
import { FieldType } from '@models/field/field-type.enum';

const initialState: DatMapMetadataModel = {
	caseId: '',
	uploadBatchId: '',
	uploadBatchTitle: null,
	lookupTables: {
		lexbeFields: [],
		datColumns: []
	},
	defaultMappings: [],
	actualMappings: [],
	loading: false,
	error: false,
	lastRunReport: null
};

export class DatMapSaveParams {
	uploadBatchId: string;
	runDatMap: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class DatMapStore extends ComponentStore<DatMapMetadataModel> {

	constructor(private datMapSdk: DatMapSdk) {
		super(initialState);
	}

	public lastRunReport$: Observable<string> = this.select(state => state.lastRunReport);
	public uploadBatchTitle$: Observable<string> = this.select(state => state.uploadBatchTitle ?? 'Dat Map');
	public lexbeFields$: Observable<LexbeField[]> = this.select(state => state.lookupTables.lexbeFields.filter(x => x.isDatMapOption));
	public datColumnMetadata$: Observable<DatColumnMetadata[]> = this.select<DatColumnMetadata[]>(state => state.lookupTables.datColumns);
	public actualMappings$: Observable<DatMapMetadataItem[]> = this.select(state => state.actualMappings);
	public mappingCount$: Observable<number> = this.select(state => state.actualMappings.length);
	public loading$: Observable<boolean> = this.select(state => state.loading);
	public error$: Observable<boolean> = this.select(state => state.error);

	public readonly clearState = this.updater(() => initialState);

	public readonly addLexbeField = this.updater((state,customField: LexbeField) =>
		produce<DatMapMetadataModel>(state, draft => {
			draft.lookupTables.lexbeFields.push(customField);
			return draft;
		})
	);

	public readonly resetMappingsToDefault = this.updater((state) => produce<DatMapMetadataModel>(state, draft => {
		draft.actualMappings = original(draft.defaultMappings);
		this.setDisabledFields(draft);
		return draft;
	}));

	public readonly updateActualMapping = this.updater((state: DatMapMetadataModel, newMapping: DatMapMetadataItem): DatMapMetadataModel =>
		produce<DatMapMetadataModel>(state, draft => {
			const newColumn = newMapping.datMapColumns[0]; // Always only 1
			const indexContainingDatColumn = draft.actualMappings.findIndex(m => m.datMapColumns.includes(newColumn)); // find actual mapping that already includes this column
			if (indexContainingDatColumn !== -1) { // This column is already mapped, so remove it from the actual mappings first
				const mapping = draft.actualMappings[indexContainingDatColumn];
				if (mapping.datMapColumns.length == 1) { // if the mapping only contains this column, remove the actual mapping
					draft.actualMappings.splice(indexContainingDatColumn, 1);
				} else { // if the mapping has other columns in it, remove the column from that mapping by finding it
					const columnIndex = mapping.datMapColumns.indexOf(newColumn);
					mapping.datMapColumns.splice(columnIndex, 1);
				}
			}
			const indexContainingLexbeField = draft.actualMappings.findIndex(m => m.lexbeField?.fieldId == newMapping?.lexbeField?.fieldId); // Find if any mapping is already targeting this lexbe field
			if (indexContainingLexbeField !== -1) { // Mapping for this field is already added so add those columns to this new mapping instance and remove the old mapping
				newMapping.datMapColumns.unshift(...draft.actualMappings[indexContainingLexbeField].datMapColumns); // Add old columns to the front of the new mapping
				draft.actualMappings.splice(indexContainingLexbeField, 1, newMapping); // Replace old mapping with new mapping
			} else if (newMapping.lexbeField !== undefined) { // undefined means delete and should never be added to mappings
				draft.actualMappings.push(newMapping);
			}

			this.setDisabledFields(draft);
			return draft;
		}));

	private setDisabledFields(draft: WritableDraft<DatMapMetadataModel>) {
		draft.lookupTables.lexbeFields.forEach(lf => {
			const map = draft.actualMappings.find(m => m.lexbeField.fieldId == lf.fieldId);
			if(map) {
				const isDate = map.lexbeField.type == FieldType.BuiltInFieldDate || map.lexbeField.type == FieldType.DateTime;
				const selectionCount = map.datMapColumns.length;
				if(isDate)
					lf.disabled = selectionCount >= 2;
				else
					lf.disabled = selectionCount >= 1;
			} else
				lf.disabled = false;
		});
	}

	public readonly getState = this.effect<string>((trigger$) => {
		return trigger$.pipe(
			tap(() => this.patchState({loading: true})),
			switchMap((uploadBatchId) => {
				return this.datMapSdk.getState(uploadBatchId).pipe(
					tapResponse(
						(datMapMetadataModel: DatMapMetadataModel) =>
							this.patchState(() =>
								produce<DatMapMetadataModel>(datMapMetadataModel, draft => {
									this.setDisabledFields(draft);
									draft.loading = false;
									draft.error = false;
									// Null strings aren't coming across the wire so we have to check manually, could fix this by always sending nulls, it's a setting
									if(!datMapMetadataModel.uploadBatchTitle)
										draft.uploadBatchTitle = null;
									return draft;
								})),
						() => {
							this.setState({
								caseId: '',
								uploadBatchId: '',
								uploadBatchTitle: null,
								lookupTables: {
									lexbeFields: [],
									datColumns: []
								},
								defaultMappings: [],
								actualMappings: [],
								loading: false,
								error: true,
								lastRunReport: null
							});
						})
				);
			})
		);
	});

	public readonly putState = this.effect<DatMapSaveParams>((trigger$) => {
		return trigger$.pipe(
			tap(() => this.patchState({loading: true})),
			switchMap((datMapSaveParams) => {
				const state = produce<DatMapMetadataModel>(this.get(), draft => {
					delete draft.loading;
					delete draft.error;
					return draft
				});
				return this.datMapSdk.putState(datMapSaveParams.uploadBatchId, state, datMapSaveParams.runDatMap).pipe(
					tapResponse(
						() =>
							this.patchState({
								loading: false,
								error: false
							}),
						() => {
							this.patchState({
								loading: false,
								error: true
							})
						})
				);
			})
		);
	});
}
